import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import MyLoans from "../components/myLoans";
import {
  Typography,
  Spin,
  Modal,
  Button,
  Form,
  InputNumber,
  message,
  Select,
  Radio,
  Space,
} from "antd";
import { useTranslation } from "react-i18next";
import PlatformMainButton from "../components/PlatformMainButton";
import { fetchLoans, createLoan, fetchOvernightInternalBalances } from "../helpers/api";
import { RootState } from "../services/store";
import { Loan, LoanFormValues, AvailableLoan } from "../types";
import {
  BORROW_FORM_CURRENCY_ORDER,
  SupportedCurrency,
  borrowAmountLimits,
  currencyIcon,
  currencyLabel,
} from "../constants/networks";

const { Title, Text } = Typography;
const { Option } = Select;

type LoanInsureProps = {
  inline?: boolean;
};

const LoanInsure: FC<LoanInsureProps> = ({ inline = false }) => {
  const { t } = useTranslation();
  const multichainForm = inline;

  const { tonPrice } = useSelector((state: RootState) => state.ton);
  const [myLoans, setMyLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [availableLoans, setAvailableLoans] = useState<AvailableLoan[]>([]);
  const [tokenType, setTokenType] = useState<string>("USDT");
  const [filteredLoans, setFilteredLoans] = useState<AvailableLoan[]>([]);
  const [assetPrices, setAssetPrices] = useState<Record<string, number | null>>({});
  const [form] = Form.useForm();

  useEffect(() => {
    const getLoans = async () => {
      setLoading(true);
      try {
        const data = await fetchLoans(page, itemsPerPage);
        setMyLoans(data.myLoans);
        setAvailableLoans(data.availableLoans);
      } catch (error) {
        console.error("Error fetching loans", error);
      } finally {
        setLoading(false);
      }
    };

    getLoans();
  }, [page, itemsPerPage, form]);

  useEffect(() => {
    if (!multichainForm) return;
    let cancelled = false;
    fetchOvernightInternalBalances(true)
      .then((r) => {
        if (!cancelled && r?.prices) setAssetPrices(r.prices);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [multichainForm]);

  useEffect(() => {
    const newFilteredLoans = multichainForm
      ? availableLoans.filter((loan) => loan.currency === tokenType)
      : availableLoans.filter((loan) => {
          if (tokenType === "USDT") {
            return loan.currency === "USDT";
          }
          return loan.currency !== "USDT";
        });
    setFilteredLoans(newFilteredLoans);
    form.resetFields(["loanId"]);
  }, [tokenType, availableLoans, form, multichainForm]);

  const selectedCurrency = multichainForm
    ? (tokenType as SupportedCurrency)
    : tokenType === "USDT"
      ? "USDT"
      : "TON";

  const amountLimits = useMemo(() => {
    if (!multichainForm) {
      if (tokenType === "USDT") {
        return { min: 50, max: 1000, hint: "Min. 50 USDT - Max. 1000 USDT" };
      }
      return {
        min: 1,
        max: 200,
        hint: `Min. 1 GRAM - Max. ${tonPrice ? (1000 / tonPrice).toFixed(0) : "…"} GRAM`,
      };
    }
    return borrowAmountLimits(selectedCurrency, tonPrice, assetPrices);
  }, [multichainForm, tokenType, selectedCurrency, tonPrice, assetPrices]);

  const handleLoanClick = () => {
    if (multichainForm) {
      setFormOpen(true);
      form.setFieldsValue({ currency: tokenType });
      return;
    }
    setModalVisible(true);
  };

  const closeForm = () => {
    if (multichainForm) {
      setFormOpen(false);
      return;
    }
    setModalVisible(false);
  };

  const handleFormSubmit = async (values: LoanFormValues) => {
    setSubmitLoading(true);
    try {
      await createLoan(values).then((res) => {
        if (res.status === "success") {
          message.success(
            t("The loan application has been successfully submitted")
          );
          form.resetFields();
          closeForm();
          setAvailableLoans((prevAvailableLoans) =>
            prevAvailableLoans.filter((loan) => loan.loan_id !== values.loanId)
          );
        } else {
          const errorMessage = res.message;
          const availableAmount = res.availableAmount;
          const availableTerm = res.availableTerm;
          const amountLabel = multichainForm
            ? currencyLabel(values.currency)
            : "GRAM";
          if (availableAmount) {
            message.error(
              t(`${errorMessage}. Available amount: ${availableAmount} ${amountLabel}`)
            );
          } else if (availableTerm) {
            message.error(
              t(`${errorMessage}. Available term: ${availableTerm} days`)
            );
          } else {
            message.error(t(errorMessage));
          }
        }
      });
    } catch (error: any) {
      message.error(t("Error submitting loan"));
    }
    setSubmitLoading(false);
  };

  const currencyField = multichainForm ? (
    <Form.Item
      name="currency"
      label={<span className="kf-label">{t("Select currency")}</span>}
      rules={[{ required: true, message: t("Please select a currency") as string }]}
      initialValue="USDT"
    >
      <Select
        size="large"
        onChange={(value: SupportedCurrency) => {
          setTokenType(value);
        }}
      >
        {BORROW_FORM_CURRENCY_ORDER.map((code) => (
          <Option key={code} value={code}>
            <Space size={6}>
              <img
                src={currencyIcon(code)}
                alt={code}
                width={16}
                height={16}
                style={{ objectFit: "contain" }}
              />
              <Text strong>{currencyLabel(code)}</Text>
            </Space>
          </Option>
        ))}
      </Select>
    </Form.Item>
  ) : (
    <Form.Item
      name="currency"
      label={t("Select currency")}
      rules={[{ required: true, message: "Please select a currency" }]}
    >
      <Radio.Group
        defaultValue="USDT"
        buttonStyle="solid"
        style={{ marginBottom: 5 }}
        onChange={(e) => setTokenType(e.target.value)}
        value={tokenType}
      >
        <Radio.Button value="USDT">USDT</Radio.Button>
        <Radio.Button value="TON">GRAM</Radio.Button>
      </Radio.Group>
    </Form.Item>
  );

  const formBody = (
    <Form
      id="loanForm"
      form={form}
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={{ currency: "USDT" }}
    >
      {currencyField}

      <Form.Item
        name="loanId"
        label={multichainForm ? <span className="kf-label">{t("Available certificates")}</span> : t("Available certificates")}
        rules={[{ required: true, message: "Please select a loan" }]}
      >
        <Select
          placeholder={t("Select a certificate")}
          style={{ width: "100%" }}
          size={multichainForm ? "large" : undefined}
          notFoundContent={t("No data")}
        >
          {filteredLoans.map((loan) => (
            <Option key={loan.loan_id} value={loan.loan_id}>
              {multichainForm
                ? `#${loan.loan_id} · ${loan.amount} ${currencyLabel(loan.currency)}`
                : `Loan ID: ${loan.loan_id} - Amount: ${loan.amount}`}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="amount"
        label={
          multichainForm ? (
            <span className="kf-label">
              {t("Amount")} ({currencyLabel(selectedCurrency)})
            </span>
          ) : (
            t("Amount")
          )
        }
        extra={amountLimits.hint}
        rules={[{ required: true, message: t("Please enter the amount") }]}
      >
        <InputNumber<number>
          min={amountLimits.min}
          max={amountLimits.max}
          formatter={(value) => `${value}`}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, ""))}
          style={{ width: multichainForm ? "100%" : "calc(100% - 30px)" }}
          size={multichainForm ? "large" : undefined}
          addonBefore={
            multichainForm ? undefined : (
              <img
                src={
                  tokenType === "USDT"
                    ? "./assets/UsdtLogo.webp"
                    : "./assets/gramIcon.png"
                }
                alt={tokenType === "USDT" ? "USDT Logo" : "GRAM Logo"}
                width={12}
              />
            )
          }
        />
      </Form.Item>

      <Form.Item
        name="term"
        label={multichainForm ? <span className="kf-label">{t("Term (days)")}</span> : t("Term (days)")}
        rules={[{ required: true, message: t("Please enter the term") }]}
      >
        <InputNumber min={1} max={30} style={{ width: "100%" }} size={multichainForm ? "large" : undefined} />
      </Form.Item>

      <Form.Item
        name="rate"
        label={multichainForm ? <span className="kf-label">{t("Rate")}</span> : t("Rate")}
        rules={[{ required: true, message: t("Please enter the rate") }]}
      >
        <InputNumber min={0.01} step={0.01} style={{ width: "100%" }} size={multichainForm ? "large" : undefined} />
      </Form.Item>

      {multichainForm ? (
        <div className="kf-borrow-actions">
          <button
            type="button"
            className="kf-borrow-cancel"
            onClick={closeForm}
            disabled={submitLoading}
          >
            {t("Cancel")}
          </button>
          <button type="submit" className="kf-primary" disabled={submitLoading}>
            {t("Submit")}
          </button>
        </div>
      ) : null}
    </Form>
  );

  const showApplyButton = multichainForm ? !formOpen : !modalVisible;

  return (
    <div style={inline ? undefined : { padding: "20px", backgroundColor: "#fff" }}>
      {!inline ? <Title level={3}>{t("Take out a loan")}</Title> : null}
      {loading ? (
        <Spin size="large" />
      ) : (
        <>
          <MyLoans loans={myLoans} />
        </>
      )}

      {multichainForm && formOpen ? (
        <div className="kf-borrow-form">{formBody}</div>
      ) : null}

      {!multichainForm ? (
        <Modal
          title={t("Apply for a Loan")}
          open={modalVisible}
          onCancel={closeForm}
          footer={[
            <Button key="cancel" onClick={closeForm}>
              {t("Cancel")}
            </Button>,
            <Button
              key="submit"
              type="primary"
              form="loanForm"
              htmlType="submit"
              loading={submitLoading}
            >
              {t("Submit")}
            </Button>,
          ]}
        >
          {formBody}
        </Modal>
      ) : null}

      {showApplyButton &&
        (inline ? (
          <button
            type="button"
            className="kf-primary"
            onClick={handleLoanClick}
            disabled={loading}
          >
            {t("Take out a loan")}
          </button>
        ) : (
          <PlatformMainButton
            text={t("Take out a loan")}
            onClick={handleLoanClick}
            disabled={loading}
          />
        ))}
    </div>
  );
};

export default LoanInsure;
